import { useState, type ReactNode } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { t } from '@web/i18n';
import { Button, Input, Label } from '@web/ui/core';
import { useDebouncedValue } from '@web/ui/core/hooks';

const MIN_TERM = 2;

/** Busca no servidor e escolhe um item; escolhido, mostra o item e o botão de trocar. */
export function SearchPicker<T>(props: {
  id: string;
  label: string;
  placeholder: string;
  value: T | null;
  onChange: (value: T | null) => void;
  queryKey: (term: string) => QueryKey;
  search: (term: string) => Promise<T[]>;
  keyOf: (item: T) => string;
  labelOf: (item: T) => string;
  /** Complemento à direita do item (ex.: a origem do solicitante). */
  badgeOf?: (item: T) => ReactNode;
}): React.JSX.Element {
  const [q, setQ] = useState('');
  const term = useDebouncedValue(q, 300);
  const matches = useQuery({
    queryKey: props.queryKey(term),
    queryFn: () => props.search(term),
    enabled: props.value === null && term.length >= MIN_TERM,
    meta: { silent: true },
  });

  if (props.value !== null) {
    const chosen = props.value;
    return (
      <div className="flex flex-col gap-1">
        <span id={props.id} className="text-meta text-muted-foreground">
          {props.label}
        </span>
        <div className="flex items-center gap-2" aria-labelledby={props.id} role="group">
          <span className="font-medium text-foreground">{props.labelOf(chosen)}</span>
          {props.badgeOf?.(chosen)}
          <span className="ml-auto">
            <Button
              size="sm"
              variant="outline"
              aria-label={`${t('mice.form.change')} ${props.label}`}
              onClick={() => {
                setQ('');
                props.onChange(null);
              }}
            >
              {t('mice.form.change')}
            </Button>
          </span>
        </div>
      </div>
    );
  }

  const items = matches.data ?? [];
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={props.id}>{props.label}</Label>
      <Input
        id={props.id}
        type="search"
        value={q}
        placeholder={props.placeholder}
        onChange={(e) => {
          setQ(e.target.value);
        }}
      />
      {term.length < MIN_TERM ? (
        <p className="text-meta text-muted-foreground">{t('mice.form.searchHint')}</p>
      ) : matches.isError ? (
        <p className="text-meta text-tone-red-ink">{t('mice.form.loadFailed')}</p>
      ) : matches.isSuccess && items.length === 0 ? (
        <p className="text-meta text-muted-foreground">{t('mice.form.noResults')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {items.map((item) => (
            <li key={props.keyOf(item)}>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-base text-foreground hover:bg-muted"
                onClick={() => {
                  props.onChange(item);
                }}
              >
                <span className="truncate">{props.labelOf(item)}</span>
                {props.badgeOf?.(item)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
